/**
 * export-forecast-excel.ts
 *
 * Exports the Project Forecast report data to a formatted Excel (.xlsx) file.
 *
 * - Exports the full filtered + sorted dataset (all rows, not just the current page)
 * - Raw numeric values written as numbers so Excel can calculate on them
 * - File named "Project_Forecast_Report_YYYY-MM-DD.xlsx"
 *
 * Dependencies:
 *  - exceljs (already in package.json)
 */

import ExcelJS from 'exceljs';
import type { ProjectForecastData } from '@/types/reports.types';
import { calculateEAC, formatDate } from '@/lib/utils';
import type { EacMethod } from '@/lib/utils';

// ── Brand colours ────────────────────────────────────────────────────────────

const HEADER_BG   = '1F3864';
const HEADER_TEXT = 'FFFFFF';
const ALT_ROW_BG  = 'F5F7FA';

// ── Column definitions ───────────────────────────────────────────────────────

interface ColDef {
  header: string;
  key: string;
  width: number;
  numFmt?: string;
  getValue: (row: ProjectForecastData, eacMethod: EacMethod) => string | number | null;
}

const COLUMNS: ColDef[] = [
  {
    header: 'Health',
    key: 'health',
    width: 12,
    getValue: (r) => r.health === 'green' ? 'On Track' : r.health === 'amber' ? 'At Risk' : 'Off Track',
  },
  {
    header: 'Project Name',
    key: 'projectName',
    width: 32,
    getValue: (r) => r.projectName,
  },
  {
    header: 'Project Code',
    key: 'projectCode',
    width: 16,
    getValue: (r) => r.projectCode || '—',
  },
  {
    header: 'Client',
    key: 'clientName',
    width: 24,
    getValue: (r) => r.clientName,
  },
  {
    header: 'Project Manager',
    key: 'projectManager',
    width: 20,
    getValue: (r) => r.projectManager || '—',
  },
  {
    header: 'Status',
    key: 'status',
    width: 14,
    getValue: (r) => r.status || '—',
  },
  {
    header: 'Budget Cost',
    key: 'budgetCost',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.forecastInputs.totalBudget,
  },
  {
    header: 'Actual Cost',
    key: 'actualCost',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.forecastInputs.actualCosts,
  },
  {
    header: 'Completion %',
    key: 'completionPercent',
    width: 14,
    numFmt: '0.0"%"',
    getValue: (r) => r.completionPercent,
  },
  {
    header: 'EAC Cost',
    key: 'eacCost',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r, eacMethod) => {
      const budget = r.forecastInputs.totalBudget;
      if (budget <= 0) return null;
      const result = calculateEAC(r.forecastInputs, eacMethod);
      return result?.eac ?? budget;
    },
  },
  {
    header: 'Forecast Variance (£)',
    key: 'forecastVariance',
    width: 20,
    numFmt: '£#,##0.00',
    getValue: (r, eacMethod) => {
      const budget = r.forecastInputs.totalBudget;
      if (budget <= 0) return null;
      const result = calculateEAC(r.forecastInputs, eacMethod);
      const eac = result?.eac ?? budget;
      return eac - budget;
    },
  },
  {
    header: 'Forecast Variance (%)',
    key: 'forecastVariancePct',
    width: 20,
    numFmt: '0.0"%"',
    getValue: (r, eacMethod) => {
      const budget = r.forecastInputs.totalBudget;
      if (budget <= 0) return null;
      const result = calculateEAC(r.forecastInputs, eacMethod);
      const eac = result?.eac ?? budget;
      return ((eac - budget) / budget) * 100;
    },
  },
  {
    header: 'Budget Revenue',
    key: 'budgetRevenue',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.revenueData.budgetRevenue,
  },
  {
    header: 'Actual Revenue',
    key: 'actualRevenue',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.revenueData.actualSales,
  },
  {
    header: 'Budget Hours',
    key: 'budgetHours',
    width: 14,
    numFmt: '#,##0',
    getValue: (r) => r.hoursData.budgetHours,
  },
  {
    header: 'Actual Hours',
    key: 'actualHours',
    width: 14,
    numFmt: '#,##0',
    getValue: (r) => r.hoursData.actualHours,
  },
  {
    header: 'Forecast End',
    key: 'forecastEnd',
    width: 16,
    getValue: (r) => {
      const date = r.forecastEndDate ?? r.endDate ?? null;
      return date ? formatDate(date) : '—';
    },
  },
  {
    header: 'Billable',
    key: 'billable',
    width: 12,
    getValue: (r) => (r.billable ?? true) ? 'Yes' : 'No',
  },
];

// ── Numeric column keys for totals row ───────────────────────────────────────

const NUMERIC_KEYS = new Set([
  'budgetCost',
  'actualCost',
  'eacCost',
  'forecastVariance',
  'budgetRevenue',
  'actualRevenue',
  'budgetHours',
  'actualHours',
]);

// ── Main export function ─────────────────────────────────────────────────────

export async function exportForecastToExcel(
  rows: ProjectForecastData[],
  eacMethod: EacMethod,
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Projects';
  workbook.created = new Date();

  const methodLabel = eacMethod === 'simple' ? 'Simple' : eacMethod === 'performance-based' ? 'Performance-Based' : 'Re-estimate';
  const sheet = workbook.addWorksheet('Project Forecast', {
    views: [{ state: 'frozen', ySplit: 2 }],
  });

  // ── Title row ─────────────────────────────────────────────────────
  const titleRow = sheet.addRow([`Project Forecast Report (EAC Method: ${methodLabel})`]);
  titleRow.height = 28;
  const titleCell = titleRow.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: HEADER_TEXT } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.mergeCells(1, 1, 1, COLUMNS.length);

  // ── Column header row ──────────────────────────────────────────────
  sheet.columns = COLUMNS.map((col) => ({
    key: col.key,
    width: col.width,
  }));

  const headerRow = sheet.addRow(COLUMNS.map((c) => c.header));
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
    };
  });

  // ── Data rows ──────────────────────────────────────────────────────
  rows.forEach((row, idx) => {
    const values = COLUMNS.map((col) => col.getValue(row, eacMethod));
    const dataRow = sheet.addRow(values);
    dataRow.height = 18;

    const isAlt = idx % 2 === 1;
    dataRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];

      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } };
      }

      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };

      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }

      // Colour-code health column
      if (colDef?.key === 'health') {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        if (cell.value === 'Off Track') {
          cell.font = { size: 10, bold: true, color: { argb: 'DC2626' } };
        } else if (cell.value === 'At Risk') {
          cell.font = { size: 10, bold: true, color: { argb: 'D97706' } };
        } else {
          cell.font = { size: 10, bold: true, color: { argb: '16A34A' } };
        }
      }

      // Colour-code forecast variance
      if (colDef?.key === 'forecastVariance' && typeof cell.value === 'number') {
        if (cell.value > 0) {
          cell.font = { size: 10, color: { argb: 'DC2626' } };
        } else if (cell.value < 0) {
          cell.font = { size: 10, color: { argb: '16A34A' } };
        }
      }
    });
  });

  // ── Totals row ─────────────────────────────────────────────────────
  if (rows.length > 0) {
    const totals = COLUMNS.map((col) => {
      if (NUMERIC_KEYS.has(col.key)) {
        return rows.reduce((sum, r) => {
          const v = col.getValue(r, eacMethod);
          return sum + (typeof v === 'number' ? v : 0);
        }, 0);
      }
      if (col.key === 'projectName') return `Total (${rows.length} projects)`;
      return null;
    });

    const totalsRow = sheet.addRow(totals);
    totalsRow.height = 20;
    totalsRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
      cell.font = { bold: true, size: 10 };
      cell.border = {
        top: { style: 'thin', color: { argb: '9FB4D8' } },
      };
      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });
  }

  // ── Auto-filter on header row ──────────────────────────────────────
  sheet.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: COLUMNS.length },
  };

  // ── Download ───────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const fileName = `Project_Forecast_Report_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
